import gsap from "gsap"
import {} from "gsap"
import propertyCardMovementAudioFile from "@/audio/property-card-movement.mp3"
import canvasConfetti from "canvas-confetti"
import { mapDesigns } from "@mono/mapDesigns"
import {
  playPieceMovementSound,
  playCardDrawSound,
  playPoliceSound,
  playJailDropSound,
  playPropertyCardMovementSound,
  createEffectAudio,
} from "./audioManager"
import { StringMappingType } from "typescript"
import { IMoneyAnimationItem, moneyAnimationItems } from "./data"
import { activeMultiplier, activeTimings } from "./animationSpeed"
import { start } from "repl"
import { isMobile } from "./utils"
import { MotionPathPlugin } from "gsap/MotionPathPlugin"
gsap.registerPlugin(MotionPathPlugin)
//@ts-ignore
gsap.ticker.useRAF = true // Use requestAnimationFrame for better performance, especially on mobile
gsap.ticker.fps(60)
gsap.ticker.lagSmoothing(0)

export const getInBetweenCellsIdsForMovement = (start: number, end: number, mapId: string, isBackward = false) => {
  const myMap = mapDesigns.find((map) => map.id === mapId)
  if (isBackward) {
    return Array(start - end + 1)
      .fill(0)
      .map((x, i) => start - i)
  }
  if (start > end) {
    const firstPart = Array(myMap!.cells.length - start)
      .fill(0)
      .map((x, i) => i + start)
    const secondPart = Array(end + 1)
      .fill(0)
      .map((x, i) => i)
    return [...firstPart, ...secondPart]
  }
  return Array(end - start + 1)
    .fill(0)
    .map((x, i) => i + start)
}

export const getCenterPointsOfCells = (cells: number[], mapId: string) => {
  const myMap = mapDesigns.find((map) => map.id === mapId)
  return cells.map((n) => {
    const cell = document.querySelector(`#cell-${n}`) as HTMLDivElement
    const cellObj = myMap?.cells.find((cell) => cell.order === n)
    const { x, y, height, width } = cell.getBoundingClientRect()

    if (cellObj?.type === "jail") {
      return { x: x + width / 2, y: y + height - 20 }
    }
    return { x: x + width / 2, y: y + height / 2 }
  })
}

export const getRandomRoll = (): DiceRoll => {
  const random1 = Math.floor(Math.random() * 6) + 1
  const random2 = Math.floor(Math.random() * 6) + 1
  return [random1, random2]
}

export const evaluateRequiredRollValues = (diceInputVal: string) => {
  let roll: DiceRoll
  if (diceInputVal) {
    return diceInputVal.split(",").map((x) => Number(x.trim())) as DiceRoll
  } else {
    return getRandomRoll()
  }
}

export const runDiceRollAnimation = (
  diceRef1: React.RefObject<HTMLElement>,
  diceRef2: React.RefObject<HTMLElement>,
  roll: DiceRoll
) => {
  if (!diceRef1.current || !diceRef2.current) return
  var diceOne = roll[0]
  var diceTwo = roll[1]

  for (var i = 1; i <= 6; i++) {
    diceRef1.current!.classList.remove("show-" + i)
    if (diceOne === i) {
      diceRef1.current!.classList.add("show-" + i)
    }
  }

  for (var k = 1; k <= 6; k++) {
    diceRef2.current!.classList.remove("show-" + k)
    if (diceTwo === k) {
      diceRef2.current!.classList.add("show-" + k)
    }
  }
}

export const runGoToJailAnimation = ({
  jailOverlayElm,
  playerOrder,
  game,
}: {
  playerOrder: number
  jailOverlayElm: HTMLElement
  game: IGameDetails
}) => {
  return new Promise((res, rej) => {
    const gameSituation = game.situation
    const activePlayer = gameSituation?.players.find((player) => player.id === gameSituation.turn)
    const startPoint = activePlayer!.position
    // Keyed by seat, not by piece: the token a player is drawn with can differ
    // per viewer now (preferred piece), while `piece-<order>` — what Piece.tsx
    // actually puts on the element — is the same for everyone.
    const pieceElm = document.getElementById(`piece-${activePlayer?.order}`) as HTMLElement
    const pieceCoords = pieceElm.getBoundingClientRect()
    const jailArea = document.getElementById("jail-area") as HTMLElement
    const jailCoords = jailArea.getBoundingClientRect()
    const jailCenterX = jailCoords.x + jailCoords.width / 2
    const jailCenterY = jailCoords.y + jailCoords.height / 2

    const rotatedStartPoint = startPoint > 10 && startPoint < 20
    const diffX = pieceCoords.x - jailCenterX + pieceCoords.width / 2
    const diffY = pieceCoords.y - jailCenterY + pieceCoords.height / 2

    playPoliceSound().catch(console.error)
    const timeline = gsap.timeline({})
    timeline
      .set(jailOverlayElm, { opacity: 0 })

      .to(pieceElm, {
        duration: activeMultiplier() * 0.8,
        x: diffX * (rotatedStartPoint ? -1 : 1),
        y: diffY * (rotatedStartPoint ? -1 : 1),
        scale: 1, // Shrink slightly during movement
        ease: "none",
        // ease: "power4.in", // Fast acceleration for a dragged feel
      })
      .to(pieceElm, {
        duration: activeMultiplier() * 0.2,
        scale: 1, // Restore to original size before landing
        y: "+=10", // Drop effect past final position
        ease: "power2.out",
      })
      .to(pieceElm, {
        y: "-=10", // Pop back up to the final position
        duration: activeMultiplier() * 0.1,
        ease: "power2.in",
      })
      .to(pieceElm, {
        x: "+=2", // Slight shake for impact
        y: "+=2",
        duration: activeMultiplier() * 0.05,
        repeat: 2,
        yoyo: true,
        ease: "sine.inOut",
        onComplete: () => {
          // Optional: Final opacity or shadow effect on lock-in
          gsap.to(pieceElm, { opacity: 0.8 })
          playJailDropSound().catch(console.error)
        },
      })
      .set(jailOverlayElm, {
        opacity: 1,
        scale: 2,
        y: -500,
        rotate: 5,
        x: 100,
        filter: "drop-shadow(0px 30px 20px rgba(0, 0, 0, 0.5))",
      })
      .to(jailOverlayElm, {
        opacity: 1,
        scale: 1,
        y: 0,
        x: 0,
        duration: activeMultiplier() * 0.8,
        rotate: 0,
        ease: "bounce.out",
        filter: "drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.4))",
        onComplete: res,
      })
  })
  // .to(jailOverlayElm, {
  //    // delay: 0.1,
  //    y: 15, // Tiny bounce up
  //  duration: 0.1,
  //    ease: "power1.inOut",
  //    // filter: "drop-shadow(0px 8px 4px rgba(0, 0, 0, 0.2))",
  // })
  // .to(jailOverlayElm, {
  //    delay: 0.1,
  //    y: 0, // Tiny bounce up
  //    duration: 0.1,
  //    ease: "power1.inOut",
  //    // filter: "drop-shadow(0px 10px 5px rgba(0, 0, 0, 0.2))",
  // });
}
export const isCellInBetweenCells = (order: number, startOrder: number, endOrder: number) => {
  if (startOrder < endOrder) return order >= startOrder && order <= endOrder
  else return order >= startOrder || order <= endOrder
}
export const startPieceMovementAnimation = async (movementEvent: IPieceMovementEvent, game: IGameDetails) => {
  const gameSituation = game.situation
  const activePlayer = JSON.parse(
    JSON.stringify(gameSituation?.players.find((player) => player.id === gameSituation.turn))
  )
  const cellsToMoveUpon = getInBetweenCellsIdsForMovement(
    movementEvent.from,
    movementEvent.to,
    game?.settings?.mapId || "0",
    movementEvent.isBackward
  )
  const centerPoints = getCenterPointsOfCells(cellsToMoveUpon, game?.settings.mapId || "0")

  const jumpDuration = activeTimings().pieceMovementJump / 1000
  // See the note above: the element id is the seat order, not the piece id.
  const pieceElm = document.getElementById(`piece-${activePlayer?.order ?? 0}`) as HTMLElement
  const { x, y, height, width } = pieceElm.getBoundingClientRect()
  const offset = {
    x: centerPoints[0].x - (x + width / 2),
    y: centerPoints[0].y - (y + height / 2),
  }

  // Calculate total animation duration for timeout fallback
  const totalDuration = centerPoints.slice(1).length * jumpDuration + 1000 // Add 1 second buffer

  return Promise.all(
    centerPoints.slice(1).map((point, index) => {
      return new Promise((res, rej) => {
        const timeline = gsap.timeline({
          delay: index * jumpDuration, // Stagger the animations
          onUpdate: () => {
            if (timeline.progress() > 0.9) {
              playPieceMovementSound().catch(console.error)
            }
          },
        })
        const map = mapDesigns.find((map) => map.id === game.settings.mapId)
        const mapRowSize = Math.floor((map?.cells?.length || 0) / 4)
        const startPoint = movementEvent.from
        const rotatedStartPoint =
          startPoint > mapRowSize * 2 && startPoint !== mapRowSize * 2 && startPoint !== mapRowSize * 3
        const cellElm = document.getElementById(`cell-${cellsToMoveUpon[index + 1]}`) as HTMLElement
        // const tl2 = gsap.timeline({ delay: index * jumpDuration })
        // tl2.to(cellElm, {
        //   duration: 0.0001,
        //   className: "",
        //   // ease: "power1.out",
        // })
        // tl2.to(cellElm, {
        //   delay: jumpDuration,
        //   duration: 0.005,
        //   className: "low-brightness",
        //   // ease: "power1.out",
        // })
        timeline
          .to(pieceElm, {
            duration: jumpDuration / 2,
            x: (point.x - centerPoints[0].x + offset.x) * (rotatedStartPoint ? -1 : 1),
            y: (point.y - centerPoints[0].y + offset.y) * (rotatedStartPoint ? -1 : 1),
            scale: 1.45, // Scale up in the middle of the jump
            ease: "power1.out", // Easing for smooth motion
          })

          .to(pieceElm, {
            duration: jumpDuration / 2,
            scale: 1, // Scale back to normal
            onComplete: res,
          })
      })
    })
  ).then(() => {
    // Add a timeout fallback to ensure completion even if GSAP animations are throttled
    return new Promise((resolve) => {
      setTimeout(resolve, totalDuration)
    })
  })
}
export const runAuctionWonAnimation = () => {
  const particleCount = 200
  canvasConfetti({
    particleCount,
    angle: 90,
    spread: 300,
    origin: {
      x: 0.5,
      y: 0.5,
    },
  })
}
//@ts-ignore
window.runAuctionWonAnim = runAuctionWonAnimation
export const startPieceMovementAnimationWithoutJumping = async (
  movementEvent: IPieceMovementEvent,
  game: IGameDetails
) => {
  const gameSituation = game.situation
  const activePlayer = JSON.parse(
    JSON.stringify(gameSituation?.players.find((player) => player.id === gameSituation.turn))
  )
  const cellsToMoveUpon = getInBetweenCellsIdsForMovement(
    movementEvent.from,
    movementEvent.to,
    game?.settings?.mapId || "0"
  )
  const centerPoints = getCenterPointsOfCells(cellsToMoveUpon, game?.settings.mapId || "0")

  // See the note above: the element id is the seat order, not the piece id.
  const pieceElm = document.getElementById(`piece-${activePlayer?.order ?? 0}`) as HTMLElement
  const { x, y, height, width } = pieceElm.getBoundingClientRect()
  const offset = {
    x: centerPoints[0].x - (x + width / 2),
    y: centerPoints[0].y - (y + height / 2),
  }

  const timeline = gsap.timeline({})

  const map = mapDesigns.find((map) => map.id === game.settings.mapId)
  const mapRowSize = Math.floor((map?.cells?.length || 0) / 4)
  const startPoint = movementEvent.from
  const rotatedStartPoint =
    startPoint > mapRowSize * 2 && startPoint !== mapRowSize * 2 && startPoint !== mapRowSize * 3

  // Calculate total animation duration for timeout fallback
  const totalDuration = activeTimings().pieceMovementWithoutJump + 1000 // Add 1 second buffer

  return new Promise((res, rej) => {
    timeline.to(pieceElm, {
      duration: activeTimings().pieceMovementWithoutJump / 1000, // Total duration of the entire animation
      ease: "power2.inOut", // Apply global easing
      motionPath: {
        path: centerPoints.map((point) => ({
          x: (point.x - centerPoints[0].x + offset.x) * (rotatedStartPoint ? -1 : 1),
          y: (point.y - centerPoints[0].y + offset.y) * (rotatedStartPoint ? -1 : 1),
        })),
        align: "self",
        autoRotate: false,
      },
      onComplete: res,
    })
  }).then(() => {
    // Add a timeout fallback to ensure completion even if GSAP animations are throttled
    return new Promise((resolve) => {
      setTimeout(resolve, totalDuration)
    })
  })
}

// Where a drawn or played card flies to be read. On a desktop screen the card
// sits in the board's upper quarter — the dice tumble across the middle and the
// blown-up card would bury them — but on a phone that quarter is far too
// shallow for it: the board is only as tall as the (landscape) viewport, so a
// card scaled 3-4x around a point a quarter of the way down hangs off the top
// of the screen. There the card is centred on the board instead, which is also
// where the keep animation has always shown it, so drawing and playing a card
// now land on the same spot.
//
// Measured off the board the deck itself sits in, rather than off the window, so
// the point follows the board (a 100vh square sitting between the two player
// columns); walking up from the card also keeps it off the guide's scaled-down
// copy of the board, which carries the same id. Falls back to the window if the
// card is somehow outside a board.
const getCardShowcasePoint = (cardElement: HTMLElement) => {
  const boardRect = cardElement.closest("#board")?.getBoundingClientRect()
  if (!boardRect?.width || !boardRect?.height) {
    return {
      x: window.innerWidth / 2,
      y: isMobile() ? window.innerHeight / 2 : window.innerHeight / 4,
    }
  }
  return {
    x: boardRect.x + boardRect.width / 2,
    y: boardRect.y + (isMobile() ? boardRect.height / 2 : boardRect.height / 4),
  }
}

// Returns whether the animation actually started, so the caller only counts a
// card as being on screen when one really is.
export const drawCard = (
  elements: IGlobalElementsObject,
  cardId: string,
  cardType: "chance" | "community-chest"
): boolean => {
  const cardsArray = cardType === "chance" ? elements.chanceCards : elements.communityChestCards
  const cardElement = cardsArray.find((card) => card.id === cardId)?.element
  if (!cardElement) return false
  const tl = gsap.timeline()
  const { x, y, width, height } = cardElement.getBoundingClientRect()
  const { x: desiredX, y: desiredY } = getCardShowcasePoint(cardElement)

  playCardDrawSound().catch(console.error)
  tl.set(cardElement, { position: "fixed", attr: { "data-visible": "true" } })
    .to(cardElement.parentNode, { zIndex: 100, duration: 0 })
    .to(cardElement.parentNode?.parentNode as HTMLElement, {
      zIndex: 100,
      duration: 0,
    })
    .to(cardElement, {
      duration: activeMultiplier() * 0.4,
      rotationY: 180,
      rotationZ: 0,
      ease: "power4.inOut",
      x: desiredX - x - width / 2,
      y: desiredY - y - height / 2,
      transformPerspective: 1500,
      transformOrigin: "center center",
      scale: isMobile() ? 2.7 : 3.7,
      boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.3)",
      zIndex: 100,
    })

    .to(cardElement, {
      delay: activeMultiplier() * 2.0,
      // Return animation to move the card back under the deck
      duration: 0.4,
      rotationY: 0,
      x: 0,
      y: 0,
      rotateZ: -90,
      scale: 1,
      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
      onComplete: () => {
        // Optional: reset position to absolute after the animation
        gsap.set(cardElement, {
          position: "absolute",
          zIndex: 0,
          // This animation never dims the card, but it shares the element with
          // the keep animation, which fades it on the way into the player's
          // hand — so put the opacity back rather than inheriting whatever the
          // last animation on this card left.
          opacity: 1,
          attr: { "data-visible": "false" },
        })

        gsap.set(cardElement.parentNode, {
          zIndex: 0,
        })

        gsap.set(cardElement.parentNode!.parentNode, {
          zIndex: 0,
        })
      },
    })
  return true
}

export const drawAndKeepCard = (
  elements: IGlobalElementsObject,
  cardId: string,
  cardType: "chance" | "community-chest",
  playerOrder: PlayerOrder
) => {
  const onMobile = isMobile()

  const cardsArray = cardType === "chance" ? elements.chanceCards : elements.communityChestCards
  const cardElement = cardsArray.find((card) => card.id === cardId)?.element
  if (!cardElement) return
  const tl = gsap.timeline()
  const { x, y, width, height } = cardElement.getBoundingClientRect()
  const { x: desiredX, y: desiredY } = getCardShowcasePoint(cardElement)

  const diffCenterX = desiredX - x - width / 2
  const diffCenterY = desiredY - y - height / 2
  // Get the player's card image element
  const playerCardImage = document.querySelector(`[data-testid="game.player-cards-btn-${playerOrder}"]`) as HTMLElement
  if (!playerCardImage) return

  const totalTime = activeTimings().drawAndKeepCard
  playCardDrawSound().catch(console.error)
  tl.set(cardElement, {
    position: "fixed",
    opacity: 1,
    attr: { "data-visible": "true" },
  })
    .to(cardElement.parentNode, { zIndex: 100, duration: 0 })
    .to(cardElement.parentNode?.parentNode as HTMLElement, {
      zIndex: 100,
      duration: 0,
    })
    .to(cardElement.parentNode, { zIndex: 100, duration: 0 })
    .to(cardElement.parentNode?.parentNode?.parentNode as HTMLElement, {
      zIndex: 100,
      duration: 0,
    })
    .to(cardElement, {
      duration: activeMultiplier() * 0.4,
      rotationY: 180,
      rotationZ: 0,
      ease: "power4.inOut",
      x: desiredX - x - width / 2,
      y: desiredY - y - height / 2,
      transformPerspective: 1500,
      transformOrigin: "center center",
      scale: onMobile ? 3.5 : 2.7,
      boxShadow: "rgba(0, 0, 0, 0.35) 0px 10px 7px 7px",
      zIndex: 100,
    })

    .to(cardElement, {
      delay: activeMultiplier() * 1.4,
      duration: 0.4,
      scale: 0.3,
      x: playerCardImage.getBoundingClientRect().x - x - width / 2,
      y: playerCardImage.getBoundingClientRect().y - y - height / 2,
      ease: "power2.inOut",
      onComplete: () => {
        tl.to(cardElement, {
          duration: 0.2,
          opacity: 0.5,
          onComplete: () => {
            // The deck is a stack of persistent nodes and every card animation
            // borrows this same element, so the flight has to be undone in
            // full — same resting state drawCard/playActionCard return to.
            // Restoring only the position left the card parked in the deck at
            // 30% scale, half-transparent and face up: a stray mini card on
            // top of the deck that stayed there until that card id was drawn
            // or played again.
            gsap.set(cardElement, {
              position: "absolute",
              zIndex: 0,
              x: 0,
              y: 0,
              z: 0,
              scale: 1,
              rotationY: 0,
              rotateZ: -90,
              opacity: 1,
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
              attr: { "data-visible": "false" },
            })

            gsap.set(cardElement.parentNode, {
              zIndex: 0,
            })

            gsap.set(cardElement.parentNode!.parentNode, {
              zIndex: 0,
            })
            gsap.set(cardElement.parentNode!.parentNode!.parentNode, {
              zIndex: 0,
            })
          },
        })
      },
    })
}
export const showChanceCard = async (chance: IBonusCard) => {}
export const getPlayerOrderFromPlayerId = (game: IGameDetails, id: string): PlayerOrder =>
  game.situation?.players.find((player) => player.id === id)?.order || 0

export const getPlayerListItemFromOrder = (order: number) => document.querySelector(`#player-list-item-${order}`)

export const getCurvePointForMoneyAnimation = (startX: number, startY: number, endX: number, endY: number) => {
  const midX = (startX + endX) / 2
  const midY = (startY + endY) / 2
  const dx = endX - startX
  const dy = endY - startY
  const dist = Math.hypot(dx, dy) || 1

  // Offset the control point perpendicular to the travel direction so the arc
  // bows consistently regardless of which way the money is going. The height
  // scales with distance (clamped) so short and long transfers both look right.
  let perpX = -dy / dist
  let perpY = dx / dist
  // Always bow "upward" (toward the top of the screen) for a natural toss.
  if (perpY > 0) {
    perpX = -perpX
    perpY = -perpY
  }
  const arc = Math.min(Math.max(dist * 0.22, 70), 240)
  return { x: midX + perpX * arc, y: midY + perpY * arc }
}

/**
 * Calculate a point on a quadratic bezier curve at a given progress (0-1)
 * This is much more performant than SVG Motion Path on mobile devices
 */
export const getBezierPoint = (
  startX: number,
  startY: number,
  controlX: number,
  controlY: number,
  endX: number,
  endY: number,
  t: number
) => {
  const mt = 1 - t
  const mt2 = mt * mt
  const t2 = t * t

  return {
    x: mt2 * startX + 2 * mt * t * controlX + t2 * endX,
    y: mt2 * startY + 2 * mt * t * controlY + t2 * endY,
  }
}
export const dividePriceIntoBills = (price: number): IMoneyAnimationItem[] => {
  if (price <= 0) return []
  const moneyAnimationItemsSorted = [...moneyAnimationItems].sort((a, b) => b.value - a.value)
  let remainingPrice = price
  const result: IMoneyAnimationItem[] = []
  let i = 0

  while (remainingPrice !== 0) {
    const currentItem = moneyAnimationItemsSorted[i]

    if (currentItem?.value <= remainingPrice) {
      remainingPrice -= currentItem.value
      result.push({ ...currentItem })
    } else {
      i++
    }
  }
  return result
}

export const playActionCard = (
  elements: IGlobalElementsObject,
  cardId: string,
  cardType: "chance" | "community-chest",
  playerOrder: PlayerOrder
) => {
  const cardsArray = cardType === "chance" ? elements.chanceCards : elements.communityChestCards
  const cardElement = cardsArray.find((card) => card.id === cardId)?.element
  if (!cardElement) return

  // Get the player's card image element
  const playerCardImage = document.querySelector(`[data-testid="game.player-cards-btn-${playerOrder}"]`) as HTMLElement
  if (!playerCardImage) return

  const tl = gsap.timeline()
  const { x, y, width, height } = cardElement.getBoundingClientRect()
  const { x: desiredX, y: desiredY } = getCardShowcasePoint(cardElement)

  playCardDrawSound().catch(console.error)

  // Start from player's card image position
  tl.set(cardElement, {
    position: "fixed",
    attr: { "data-visible": "true" },
    x: playerCardImage.getBoundingClientRect().x - x,
    y: playerCardImage.getBoundingClientRect().y - y,
    scale: 0.3,
    zIndex: 100,
    opacity: 1,
  })
    .to(cardElement.parentNode, { zIndex: 100, duration: 0 })
    .to(cardElement.parentNode?.parentNode as HTMLElement, {
      zIndex: 100,
      duration: 0,
    })
    .to(cardElement.parentNode?.parentNode?.parentNode as HTMLElement, {
      zIndex: 100,
      duration: 0,
    })
    .to(cardElement, {
      duration: activeMultiplier() * 0.6,
      x: desiredX - x - width / 2,
      y: desiredY - y - height / 2,
      // Same blow-up as drawCard on a phone: the two animations now show the
      // card in the same place, so a different size there would read as the
      // played card landing somewhere else.
      scale: isMobile() ? 2.7 : 2.3,
      rotationY: 180,
      rotationZ: 0,
      transformPerspective: 1500,
      transformOrigin: "center center",
      boxShadow: "rgba(0, 0, 0, 0.35) 0px 10px 7px 7px",
      ease: "power4.inOut",
    })

    .to(cardElement, {
      delay: activeMultiplier() * 2,
      duration: 0.4,
      rotationY: 0,
      x: 0,
      y: 0,
      rotateZ: -90,
      scale: 1,
      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
      onComplete: () => {
        gsap.set(cardElement, {
          position: "absolute",
          zIndex: 0,
          attr: { "data-visible": "false" },
        })
        gsap.set(cardElement.parentNode, { zIndex: 0 })
        gsap.set(cardElement.parentNode!.parentNode, { zIndex: 0 })
        gsap.set(cardElement.parentNode!.parentNode!.parentNode, { zIndex: 0 })
      },
    })
}

// Purely-frontend FLIP animation for traded property cards. Trades commit on
// the backend instantly, so by the time the situation update renders, each
// traded card has already jumped into its new owner's panel. The caller
// captures the cards' screen positions BEFORE dispatching that update; this
// then flies the real card (no dummy/clone elements) from its old spot into
// its new container. React may commit the re-render a frame or two after
// dispatch, so we poll rAF until each card's rect actually changes before
// starting its flight.
export const runTradedCardsFlipAnimation = (
  prevRects: Map<number, DOMRect>
) => {
  let attempts = 0
  let soundPlayed = false
  const animateMovedCards = () => {
    prevRects.forEach((prevRect, order) => {
      const el = document.getElementById(`small-card-${order}`)
      if (!el) return
      const newRect = el.getBoundingClientRect()
      // Rect unchanged -> the re-render hasn't moved this card yet; retry.
      if (
        Math.abs(prevRect.left - newRect.left) < 1 &&
        Math.abs(prevRect.top - newRect.top) < 1
      )
        return
      prevRects.delete(order)
      if (!soundPlayed) {
        soundPlayed = true
        createEffectAudio(propertyCardMovementAudioFile).play().catch(() => {})
      }
      // Same flight mechanics as the buy/bankruptcy move animation: a plain
      // transform would stay clipped by the panel's overflow and stuck under
      // sibling panels' stacking contexts (the card only becomes visible for
      // the last few pixels), so instead absolute-position the card relative
      // to its offsetParent and animate top/left across the board. While the
      // card is out of flow its destination slot collapses and siblings shift;
      // clearing the props on arrival re-inserts it at exactly the measured
      // newRect position, so the landing itself is seamless.
      const offsetParent = el.offsetParent as HTMLElement | null
      const parentRect = offsetParent?.getBoundingClientRect() ?? {
        top: 0,
        left: 0,
      }
      // Park the card at its old position with a standalone gsap.set, which
      // applies synchronously — a timeline would only render on gsap's next
      // ticker tick, letting the browser paint one frame with the card already
      // sitting in its new panel (a visible jerk at flight start).
      gsap.set(el, {
        position: "absolute",
        top: prevRect.top - parentRect.top,
        left: prevRect.left - parentRect.left,
        scale: 1.1,
        zIndex: 120,
      })
      gsap.to(el, {
        top: newRect.top - parentRect.top,
        left: newRect.left - parentRect.left,
        scale: 1,
        duration:
          activeMultiplier() *
          (activeTimings().propertyTransfer / 1000),
        ease: "power2.inOut",
        onComplete: () => {
          gsap.set(el, {
            clearProps: "position,top,left,zIndex,transform",
          })
        },
      })
    })
  }
  const tick = () => {
    attempts++
    animateMovedCards()
    // Give React ~20 frames to commit; anything still unmoved by then (e.g. a
    // card that isn't rendered on this client) is simply skipped.
    if (prevRects.size && attempts < 20) requestAnimationFrame(tick)
  }
  // React may commit the re-render synchronously within the dispatch, in a
  // microtask, or in a later scheduler task depending on where the update came
  // from. Try all three windows in order — sync and microtask both run before
  // the browser can paint, and the rAF fallback also fires ahead of each
  // frame's paint — so whichever one first sees the moved card repositions it
  // before a frame with the card at its destination is ever shown.
  animateMovedCards()
  Promise.resolve().then(() => {
    if (prevRects.size) animateMovedCards()
  })
  if (prevRects.size) requestAnimationFrame(tick)
}

// Minimize a trade-detail surface (the completion toast or the center
// trade-detail modal) into its row in the trades panel's history, so players
// learn the trade details stay reviewable there. `kind` picks which row the
// flight lands on in that one mixed list: a completed trade's, or a rejected
// offer's. Flies the real element to the row for
// tradeId when given (falling back to the newest row of that kind, then the
// panel itself) and flashes the row on landing. Calls onArrived exactly once;
// when no panel exists on this layout the element is dismissed with no flight.
export const runTradeMinimizeAnimation = (
  el: HTMLElement,
  onArrived: () => void,
  tradeId?: string,
  kind: "completed" | "rejected" = "completed"
) => {
  const row =
    (tradeId && document.getElementById(`${kind}-trade-row-${tradeId}`)) ||
    document.querySelector<HTMLElement>(`[data-${kind}-trade-row]`)
  const panel = document.getElementById("active-trades-panel")
  const target = row || panel
  if (!target) {
    onArrived()
    return
  }
  const from = el.getBoundingClientRect()
  const to = target.getBoundingClientRect()
  let cx = to.left + to.width / 2
  let cy = to.top + to.height / 2
  // A history row scrolled out of the panel's overflow box still reports its
  // laid-out position — clamp the landing point into the panel so the flight
  // never ends on empty space.
  const panelRect = panel?.getBoundingClientRect()
  if (panelRect) {
    cx = Math.min(Math.max(cx, panelRect.left), panelRect.right)
    cy = Math.min(Math.max(cy, panelRect.top), panelRect.bottom)
  }
  const dx = cx - (from.left + from.width / 2)
  const dy = cy - (from.top + from.height / 2)
  const scale = Math.max(
    0.12,
    Math.min(to.width / from.width, to.height / from.height)
  )
  // Both surfaces enter via a CSS pop-in animation that fills at an identity
  // end state; drop it so it can't override the tween's inline transform.
  el.style.animation = "none"
  el.style.pointerEvents = "none"
  gsap.to(el, {
    // Relative offsets so the modal's translate(-50%,-50%) centering (already
    // baked into the measured rect) is preserved as the starting point.
    x: `+=${dx}`,
    y: `+=${dy}`,
    scale,
    opacity: 0,
    duration: 0.55,
    ease: "power2.in",
    onComplete: () => {
      onArrived()
      // Flash the landing row so the eye follows the flight into the list.
      if (row) {
        const landedClass = `${kind}-trade-landed`
        row.classList.add(landedClass)
        setTimeout(() => row.classList.remove(landedClass), 1000)
      }
    },
  })
}
